import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Video } from "lucide-react";
import { IMAGES } from "../data/site";
import { scrollTo } from "./Navbar";

const LINES = ["Riconnettersi con sé stessi,", "per abitare il presente", "con sereno equilibrio."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden grain-overlay pt-[72px]" data-testid="hero-section">
      <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-[#EAE4D8] blur-3xl opacity-60 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
                    className="font-mono text-xs tracking-[0.25em] uppercase text-[#4A6B5D] mb-7" data-testid="hero-badge">
            Psicoterapia per giovani adulti · Roma Ostiense e Online
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.12] text-[#1C1E1D]">
            {["Uno spazio di ascolto", "e comprensione per", "ritrovare sé stessi."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
                             transition={{ duration: 0.9, delay: 0.3 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}>
                  {i === 2 ? <em className="text-[#C85A32] not-italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.0 }}
                    className="mt-7 text-lg font-light text-[#525754] leading-relaxed max-w-xl" data-testid="hero-subtitle">
            Sono Veronica Sciacquatori, psicologa e psicoterapeuta. Accompagno giovani adulti e adulti
            nell'affrontare ansia, esperienze traumatiche, difficoltà relazionali e momenti di cambiamento —
            riscoprendo le proprie risorse e costruendo un rapporto più consapevole con sé stessi.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.15 }}
                      className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={(e) => { e.preventDefault(); scrollTo("#contatti"); }} data-testid="hero-cta-booking"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-[#FBF9F5] font-medium px-7 py-3.5 transition-colors duration-300">
              Richiedi un primo colloquio
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button onClick={(e) => { e.preventDefault(); scrollTo("#aree"); }} data-testid="hero-cta-resources"
                    className="inline-flex items-center rounded-full border border-[#1C1E1D]/25 hover:border-[#1C1E1D] hover:bg-[#1C1E1D] hover:text-[#FBF9F5] text-sm font-medium px-7 py-3.5 transition-colors duration-300">
              Scopri i percorsi
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }} className="relative">
            <div className="absolute -inset-3 border border-[#D4A359]/50 rounded-2xl pointer-events-none" />
            <div className="overflow-hidden rounded-2xl spotlight-img">
              <motion.img src={IMAGES.portraitMain} alt="Veronica Sciacquatori, psicologa e psicoterapeuta" style={{ y: imgY }}
                          className="w-full h-[420px] lg:h-[520px] object-cover object-[50%_18%] scale-110" data-testid="hero-portrait" />
            </div>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.2 }}
                        className="absolute -bottom-6 -left-4 sm:-left-8 bg-[#FBF9F5]/90 backdrop-blur-lg border border-[#1C1E1D]/10 rounded-2xl px-5 py-4 shadow-lg"
                        data-testid="hero-stat-card">
              <p className="font-serif text-2xl text-[#1C1E1D]">Roma Ostiense</p>
              <p className="text-xs text-[#525754] mt-0.5">Via Ostiense 104B</p>
              <div className="flex gap-4 mt-2.5 text-[11px] text-[#4A6B5D] font-medium">
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> In Studio</span>
                <span className="inline-flex items-center gap-1"><Video size={12} /> Online</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
